import { useQuery } from '@apollo/client';
import { Box, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Edit } from '@styled-icons/boxicons-solid';
import defaultComponentImage from 'assets/img/components/component-default.png';
import ComponentChainTable from 'FeatureModules/ComponentChain/Components/Table/ComponentChainTable';
import ProductSupplyChainTable from 'FeatureModules/ProductPage/Components/Tables/ProductSupplyChainTable';
import { ITableRowAction } from 'FeatureModules/ComponentChain/Components/Table/TableRow';
import { ECOMPONENT_CHAIN_COLUMN_IDS } from 'FeatureModules/ComponentChain/utils/headerColumns';
import useComponentChainActions from 'FeatureModules/ComponentChain/hooks/useComponentChainActions';
import Loader from 'Components/Forms/Loader';
import OverviewHeader from 'Components/OverviewHeader/OverviewHeader';
import useDialog from 'hooks/useDialog';
import { CardIllustration, EmptyStateCard, Tabs, ThemeButton } from 'DesignSystem';
import Image from 'DesignSystem/DataDisplay/Image/Image';
import ConditionalRender from 'DesignSystem/Layout/CondionalRender/ConditionalRender';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import { ITab } from 'DesignSystem/Primitives/Tabs/Tabs';
import ThemeTypography from 'DesignSystem/Primitives/Typography/ThemeTypography';
import { GET_COMPONENT } from 'graphql/secure/queries';
import React, { FC, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Booleanish } from 'types/booleanish.types';
import { IComponentItem } from 'FeatureModules/ComponentChain/types/component.types';
import {
  EBackgroundTypes,
  EIllustrationTypes,
  EItemTypes,
} from 'DesignSystem/types/background.types';
import { EAvailableSizes } from 'DesignSystem/types/size.types';
import ErrorState from 'Components/ErrorState';
import FlexBox from 'Components/FlexBox';
import PageContainer from 'Components/PageContainer';
import PageTitle from 'Components/PageTitle';

enum IComponentOverviewTabs {
  COMPONENT_CHAINS = 'COMPONENT_CHAINS',
  PRODUCT_SUPPLY_CHAINS = 'PRODUCT_SUPPLY_CHAINS',
}

const COMPONENT_OVERVIEW_TABS: ITab[] = [
  {
    key: IComponentOverviewTabs.COMPONENT_CHAINS,
    label: 'Component chains',
    startIconName: 'component-chain',
  },
  {
    key: IComponentOverviewTabs.PRODUCT_SUPPLY_CHAINS,
    label: 'Product supply chains',
    startIconName: 'supply-chain',
  },
];

const StyledSubTitle = styled(ThemeTypography)(({ theme }) => ({
  textTransform: 'uppercase',
}));

const ComponentItemOverview: FC = () => {
  const { createComponentChain } = useComponentChainActions();
  const { openDialog } = useDialog();
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState<IComponentOverviewTabs>(
    IComponentOverviewTabs.COMPONENT_CHAINS
  );
  const { id: paramId } = useParams();

  const { data, loading, error } = useQuery<{ component: IComponentItem }>(GET_COMPONENT, {
    variables: {
      id: paramId,
    },
  });
  const component = data?.component;

  const isPartOfChain = !!(component?.componentChains?.length || component?.supplyChains?.length);

  const handleChangeTab = (tab: string) => {
    setCurrentTab(tab as IComponentOverviewTabs);
  };

  const handleCreateComponentChain = () => {
    createComponentChain();
  };

  const handleEditComponent = (
    component: Omit<IComponentItem, 'inUse' | 'lastEditedTimestamp'>
  ) => {
    openDialog({
      type: 'ADD_EDIT_COMPONENT',
      props: {
        title: 'Edit component',
        componentItem: component,
      },
    });
  };

  const handleComponentChainClick = (chainId: string) => {
    navigate(`/component-chains/${chainId}`);
  };

  const handleSupplyChainClick = (productId: string, chainId: string) => {
    navigate(`/supply-chains/active/${productId}/${chainId}`);
  };

  const handleClickPartner = (partnerId: string) => {
    navigate(`/partners/${partnerId}`);
  };

  const componentTableActions: ITableRowAction[] = [
    {
      renderIcon: () => <Edit size="17" />,
      tooltip: 'Edit component chain',
      onClick: (id: string) => navigate(`/component-chains/${id}/editor`),
    },
  ];

  const supplyChainTableActions: ITableRowAction[] = [
    {
      renderIcon: () => <Edit size="17" />,
      tooltip: 'Edit supply chain',
      onClick: (chainId: string) => {
        const productId = component?.supplyChains?.find(chain => chain.id === chainId)?.productId;
        navigate(`/supply-chains/active/${productId}/${chainId}/editor`);
      },
    },
  ];

  if (loading) return <Loader />;
  if (error || !component) return <ErrorState />;

  return (
    <PageContainer>
      <PageTitle
        title="Component overview"
        goBackLabel="All unique components"
        goBackUrl="/components"
        enableAutoGoBackHistoryButton
        titleExtension={component?.title}
      />
      <Box mt={2.5} />
      <Grid item xs={12}>
        <OverviewHeader
          placeholderImageUrl={defaultComponentImage}
          image={component?.image || undefined}
        >
          <Box display="flex" justifyContent="space-between" flex={1} height="100%">
            <Box
              display="flex"
              flexDirection="column"
              flex={1}
              justifyContent="center"
              alignItems="flex-start"
            >
              {/* Title and article number  */}
              <FlexBox mb={1}>
                <ThemeTypography variant="TITLE_MEDIUM" data-testid="component-title">
                  {component.title}
                </ThemeTypography>
                {component.articleNumber && (
                  <FlexBox ml={2}>
                    <ThemeTypography variant="TITLE_EXTRA_SMALL" color="GRAY_40">
                      |
                    </ThemeTypography>
                    <Box mr={1} />
                    <ThemeTypography
                      variant="TITLE_EXTRA_SMALL"
                      autoOverflow
                      maxWidth={200}
                      color="GRAY_40"
                    >
                      {component.articleNumber}
                    </ThemeTypography>
                  </FlexBox>
                )}
              </FlexBox>

              {/* Raw materials  */}
              <FlexBox gap={1}>
                <FlexBox gap={0.5}>
                  <Icon name="raw-material" color="gray-80" />
                  <ThemeTypography
                    variant="BODY_MEDIUM_BOLD"
                    color="GRAY_80"
                    maxWidth={300}
                    autoOverflow
                  >
                    {component.rawMaterials?.reduce(
                      (prev, rawMaterial) =>
                        prev ? `${prev}, ${rawMaterial.title}` : rawMaterial.title,
                      ''
                    )}
                  </ThemeTypography>
                </FlexBox>

                {component.ownedBy && (
                  <FlexBox
                    gap={0.5}
                    clickable={Booleanish(true)}
                    onClick={() =>
                      component.ownedBy?.id && handleClickPartner(component.ownedBy.id)
                    }
                  >
                    <Image
                      image={component.ownedBy?.logo}
                      backupIcon="partner"
                      width={12}
                      height={12}
                    />
                    <ThemeTypography variant="LABEL_INPUT" color="GRAY_80">
                      {component.ownedBy.name}
                    </ThemeTypography>
                  </FlexBox>
                )}
              </FlexBox>

              {/* Action buttons  */}
              <FlexBox mt={2.5}>
                <ThemeButton
                  startIcon={<Edit size={14} />}
                  size="large"
                  color="BLUE_ICE"
                  loading={false}
                  onClick={() => handleEditComponent(component)}
                  data-testid="edit-component-btn"
                >
                  Edit component
                </ThemeButton>
                {isPartOfChain && (
                  <Box ml={1}>
                    <ThemeButton
                      startIcon={<Icon name="component-chain" color="white" />}
                      data-testid="map-component-chain-btn"
                      size="large"
                      color="PRIMARY"
                      loading={false}
                      onClick={handleCreateComponentChain}
                    >
                      Map new component chain
                    </ThemeButton>
                  </Box>
                )}
              </FlexBox>
            </Box>
          </Box>
        </OverviewHeader>
      </Grid>

      <ConditionalRender condition={!isPartOfChain}>
        {({ True, False }) => (
          <>
            <True>
              <Box mt={2} />
              <EmptyStateCard
                title="Map your component chains to apply and re-use in your product supply chains"
                onCreateItemClick={handleCreateComponentChain}
                variant={EItemTypes.COMPONENT_CHAIN}
                buttonIcon={<Icon name="component-chain" />}
                buttonText="Add component chain"
                background={EBackgroundTypes.WORLD}
                size={EAvailableSizes.MEDIUM}
                renderIllustration={
                  <CardIllustration
                    image={EIllustrationTypes.LOCATIONS}
                    width={300}
                    right={25}
                    top={0}
                  />
                }
                data-testid="component-chains-getting-started-card"
              />
            </True>
            <False>
              <Box mt={4}>
                <StyledSubTitle variant="TITLE_EXTRA_SMALL">
                  Chains that include this component
                </StyledSubTitle>
              </Box>
              {component.componentChains?.length && component.supplyChains?.length ? (
                <>
                  <Box mt={1.5} mb={1} display="flex" justifyContent="space-between">
                    <Tabs
                      tabs={COMPONENT_OVERVIEW_TABS}
                      selectedTab={currentTab}
                      onChange={handleChangeTab}
                    />
                  </Box>
                  {currentTab === IComponentOverviewTabs.COMPONENT_CHAINS ? (
                    <ComponentChainTable
                      columnKeys={[
                        ECOMPONENT_CHAIN_COLUMN_IDS.TITLE,
                        ECOMPONENT_CHAIN_COLUMN_IDS.ACTIONS,
                      ]}
                      componentChains={component.componentChains}
                      actions={componentTableActions}
                      onRowClick={handleComponentChainClick}
                    />
                  ) : (
                    <ProductSupplyChainTable
                      productSupplyChains={component.supplyChains}
                      actions={supplyChainTableActions}
                      onRowClick={handleSupplyChainClick}
                    />
                  )}
                </>
              ) : (
                <>
                  {!!component?.componentChains?.length && (
                    <ComponentChainTable
                      columnKeys={[
                        ECOMPONENT_CHAIN_COLUMN_IDS.TITLE,
                        ECOMPONENT_CHAIN_COLUMN_IDS.ACTIONS,
                      ]}
                      componentChains={component.componentChains}
                      actions={componentTableActions}
                      onRowClick={handleComponentChainClick}
                    />
                  )}
                  {!!component?.supplyChains?.length && (
                    <ProductSupplyChainTable
                      productSupplyChains={component.supplyChains}
                      actions={supplyChainTableActions}
                      onRowClick={handleSupplyChainClick}
                    />
                  )}
                </>
              )}
            </False>
          </>
        )}
      </ConditionalRender>
    </PageContainer>
  );
};

export default ComponentItemOverview;
